/**
 * api 客户端（S04）。
 *
 * 通道：SDK callFunction 调云函数 `api`（事件模型，S03 单入口）。
 * 职责：
 *  1. 组装事件 {path, httpMethod, headers, body}
 *  2. 自动注入 Authorization（来自 auth.getSession() 的 access_token）
 *  3. POST 自动注入 Idempotency-Key（可选关闭）
 *  4. 统一包络 {code,msg,data,traceId} 解析；非 0 抛 ApiError（带 traceId）
 */

import { app, auth } from "./cloudbase";

export interface ApiEnvelope<T = unknown> {
  code: number;
  msg: string;
  data: T;
  traceId: string;
}

export class ApiError extends Error {
  code: number;
  traceId: string;
  constructor(code: number, msg: string, traceId: string) {
    super(msg);
    this.code = code;
    this.traceId = traceId;
  }
}

function traceId(): string {
  return crypto.randomUUID();
}

async function accessToken(): Promise<string | null> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data } = await (auth as any).getSession();
    return data?.session?.access_token ?? null;
  } catch {
    return null;
  }
}

export interface CallOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** POST 默认自动带 Idempotency-Key；显式传 false 关闭。 */
  idempotencyKey?: string | false;
  /** 额外请求头（如 x-tenant-id，S06 起统一从项目上下文注入）。 */
  headers?: Record<string, string>;
}

export async function callApi<T = unknown>(
  path: string,
  opts: CallOptions = {}
): Promise<ApiEnvelope<T>> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };

  const token = await accessToken();
  if (token) headers["authorization"] = `Bearer ${token}`;

  let body: string | undefined;
  if (opts.body !== undefined) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  if (method === "POST" && opts.idempotencyKey !== false) {
    headers["idempotency-key"] =
      typeof opts.idempotencyKey === "string" ? opts.idempotencyKey : traceId();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let res: any;
  try {
    res = await (app as any).callFunction({
      name: "api",
      data: { path, httpMethod: method, headers, body: body ?? null },
    });
  } catch (e) {
    // 网关层拒绝（身份或来源不被允许）时不会走到云函数，错误长得像 SDK 异常，
    // 这里翻译成看得懂的话，避免「新建项目失败」只显示一串英文。
    const raw = `${(e as { code?: string }).code ?? ""} ${(e as Error)?.message ?? e}`;
    if (/EXCEED_AUTHORITY|exceeds granted authority/i.test(raw)) {
      // 这种会话已经废了（匿名或来源受限），清掉并回登录页，别让用户反复点
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (auth as any).signOut();
      } catch {
        /* ignore */
      }
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.replace("/login");
      }
      throw new ApiError(
        2003,
        "当前身份无权调用接口（匿名登录或来源域名受限），已退出登录，请改用邮箱验证码登录。",
        "-"
      );
    }
    throw new ApiError(-1, `网络或网关异常：${(e as Error)?.message ?? String(e)}`, "-");
  }

  // 云函数 main 返回的是 HTTP 形态 { statusCode, headers, body: string }（同一份代码
  // 还要被网关 /api 路由直接使用），SDK callFunction 会把这个对象原样作为 result 带回，
  // 所以这里先拆一层 body 再当统一包络解析。
  const raw = res?.result as Record<string, unknown> | undefined;
  let env = raw as ApiEnvelope<T> | undefined;
  if (raw && typeof raw.body === "string") {
    try {
      env = JSON.parse(raw.body) as ApiEnvelope<T>;
    } catch {
      env = undefined;
    }
  }
  if (!env || typeof env.code !== "number") {
    throw new ApiError(-1, "响应格式异常（缺少统一包络）", res?.requestId ?? "-");
  }
  if (env.code !== 0) {
    throw new ApiError(env.code, env.msg, env.traceId);
  }
  return env;
}
